import { SITE } from "./site";
import { PRIMARY_LOCATIONS, SECONDARY_LOCATIONS, SATNA_CARD } from "./locations";
import { basePath, cityName, getLocalizedLocation, localizePath, translate } from "../i18n/locale";

const DOMAIN = SITE.domain.replace(/\/+$/, "");
const FARE_DESCRIPTION = "For vehicles with published rates, outstation hire includes fuel with a 300 km daily minimum. Local hire covers 120 km / 12 hours with fuel extra. GST at 5% and applicable trip charges are additional. The final quote and availability are confirmed for the itinerary.";
const FARE_DESCRIPTION_HI = "प्रकाशित दर वाले वाहनों की शहर से बाहर की यात्रा में ईंधन शामिल है और प्रतिदिन न्यूनतम 300 km लागू हैं। स्थानीय किराया 120 km / 12 घंटे के लिए है, जिसमें ईंधन अतिरिक्त है। GST 5% और लागू यात्रा खर्च अतिरिक्त हैं। यात्रा योजना के अनुसार अंतिम किराये और उपलब्धता की पुष्टि की जाती है।";
const TEMPO_DESCRIPTION = "13, 17, 20, 21 and 26 total-seat options include one driver, providing 12, 16, 19, 20 and 25 passenger seats respectively. The 9-seater option has a quotation on request.";
const SCHEMA_TEXT_HI = {
  "Tempo Traveller hire": "चालक सहित किराए पर Tempo Traveller",
  "Car rental with a driver for local packages, outstation journeys and multi-day trips; the vehicle, fare and itinerary are confirmed before booking.": "स्थानीय पैकेज, शहर से बाहर और कई दिनों की यात्रा के लिए चालक सहित किराए की कार; बुकिंग से पहले वाहन, किराये और यात्रा योजना की पुष्टि की जाती है।",
  [TEMPO_DESCRIPTION]: "कुल 13, 17, 20, 21 और 26 सीटों के विकल्पों में एक चालक शामिल है; इनमें क्रमशः 12, 16, 19, 20 और 25 यात्रियों की सीटें हैं। 9-सीटर विकल्प का किराया अनुरोध पर बताया जाता है।",
};

function localizeSchemaText(value) {
  if (value === FARE_DESCRIPTION) return FARE_DESCRIPTION_HI;
  if (value.endsWith(` ${FARE_DESCRIPTION}`)) {
    return `${localizeSchemaText(value.slice(0, -FARE_DESCRIPTION.length - 1))} ${FARE_DESCRIPTION_HI}`;
  }
  if (SCHEMA_TEXT_HI[value]) return SCHEMA_TEXT_HI[value];
  const serviceName = value.match(/^Taxi, car hire(?:, car rental)? and Tempo Traveller services in (.+)$/);
  if (serviceName) return `${cityName(serviceName[1], "hi")} में टैक्सी, किराए की कार और Tempo Traveller सेवाएँ`;
  const catalogName = value.match(/^Taxi and Tempo Traveller services for (.+)$/);
  if (catalogName) return `${cityName(catalogName[1], "hi")} के लिए टैक्सी और Tempo Traveller सेवाएँ`;
  return cityName(translate(value, "hi"), "hi");
}

export function absoluteSeoImage(image) {
  return new URL(image || "/logo.png", `${DOMAIN}/`).href;
}

// One transformation for initial HTML and client navigation. Never rewrite the
// provider record, schema types/enums, phones, numeric fares or existing @ids.
export function localizeSeoJsonLd(value, locale = "en", field = "") {
  if (value == null || field === "provider") return value;
  if (Array.isArray(value)) return value.map((item) => localizeSeoJsonLd(item, locale, field));
  if (typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, localizeSeoJsonLd(item, locale, key)]));
  }
  if (typeof value !== "string") return value;
  if (["image", "logo", "thumbnailUrl", "contentUrl"].includes(field)) return absoluteSeoImage(value);
  if (locale !== "hi") return value;
  const pageEntityId = field === "@id" && /#(?:travel-service|service-catalog|offered-service-|breadcrumbs)/.test(value);
  if ((["url", "item"].includes(field) || pageEntityId) && (value === DOMAIN || value.startsWith(`${DOMAIN}/`))) {
    return `${DOMAIN}${localizePath(value.slice(DOMAIN.length) || "/", locale)}`;
  }
  return ["name", "description", "text", "serviceType"].includes(field) ? localizeSchemaText(value) : value;
}

export function buildPageSeoSchemas({ pathname, title, description, image, jsonLd, locale = "en", noindex = false }) {
  if (noindex) return [];
  const route = localizePath(pathname, locale);
  const path = basePath(route);
  const url = `${DOMAIN}${route}`;
  const pageType = path === "/service-areas/" ? "CollectionPage" : path === "/about/" ? "AboutPage" : path === "/contact/" ? "ContactPage" : "WebPage";
  const entities = [].concat(localizeSeoJsonLd(jsonLd, locale) || []).filter(Boolean);
  let destinationList;
  if (path === "/service-areas/") {
    // Same order and records as the visible primary/secondary location cards.
    const destinations = [SATNA_CARD, ...PRIMARY_LOCATIONS, ...SECONDARY_LOCATIONS];
    destinationList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": url + "#destinations",
      name: locale === "hi" ? "टैक्सी सेवा के क्षेत्र और यात्रा के गंतव्य" : "Taxi service areas and travel destinations",
      numberOfItems: destinations.length,
      itemListOrder: "https://schema.org/ItemListUnordered",
      itemListElement: destinations.map((record, index) => {
        const localized = getLocalizedLocation(record, locale);
        return {
          "@type": "ListItem", position: index + 1,
          name: localized.displayName || localized.cityLabel || record.city,
          url: DOMAIN + localizePath(record.slug, locale),
        };
      }),
    };
    entities.push(destinationList);
  }
  const mainService = entities.find(entity => ["Service", "TaxiService"].includes(entity["@type"]) && entity["@id"]);
  const breadcrumbs = entities.find(entity => entity["@type"] === "BreadcrumbList" && entity["@id"]);
  const page = {
    "@context": "https://schema.org",
    "@type": pageType,
    "@id": `${url}#webpage`,
    url,
    name: translate(title, locale),
    description: translate(description, locale),
    inLanguage: locale === "hi" ? "hi-IN" : "en-IN",
    image: absoluteSeoImage(image),
    ...(destinationList ? { mainEntity: { "@id": destinationList["@id"] }, ...(mainService ? { about: { "@id": mainService["@id"] } } : {}) } : mainService ? { mainEntity: { "@id": mainService["@id"] } } : {}),
    ...(breadcrumbs ? { breadcrumb: { "@id": breadcrumbs["@id"] } } : {}),
    isPartOf: {
      "@type": "WebSite",
      "@id": `${DOMAIN}/#website`,
      url: `${DOMAIN}/`,
      name: SITE.brand,
    },
    publisher: {
      "@type": "LocalBusiness",
      "@id": `${DOMAIN}/#localbusiness`,
      name: SITE.brand,
      telephone: SITE.phonePrimary.tel,
      email: SITE.email,
      address: {
        "@type": "PostalAddress",
        addressLocality: SITE.baseCity,
        addressRegion: SITE.region,
        addressCountry: "IN",
      },
    },
  };
  return [page, ...entities];
}
